import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { 
  Subscription, 
  SubscriptionTier, 
  BillingFrequency, 
  PRICING_TIERS 
} from '../../types/billing';
import { ToolkitSelection } from './ToolkitSelection';

interface ChangePlanDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  subscription: Subscription | null;
  selectedTier: SubscriptionTier;
  setSelectedTier: (tier: SubscriptionTier) => void;
  selectedFrequency: BillingFrequency;
  setSelectedFrequency: (frequency: BillingFrequency) => void;
  selectedToolkit: string;
  setSelectedToolkit: (toolkit: string) => void;
  newUserCount: number;
  setNewUserCount: (count: number) => void;
  loading: boolean;
}

export const ChangePlanDialog: React.FC<ChangePlanDialogProps> = ({
  open,
  onClose,
  onConfirm,
  subscription,
  selectedTier,
  setSelectedTier,
  selectedFrequency,
  setSelectedFrequency,
  selectedToolkit,
  setSelectedToolkit,
  newUserCount,
  setNewUserCount,
  loading,
}) => {
  // Reset toolkit selection when tier changes away from Industry Specific
  useEffect(() => {
    if (selectedTier !== SubscriptionTier.INDUSTRY_SPECIFIC) {
      setSelectedToolkit('');
    }
  }, [selectedTier, setSelectedToolkit]);

  const calculatePrice = () => {
    const tierInfo = PRICING_TIERS.find(t => t.tier === selectedTier);
    if (!tierInfo) return 0;

    let basePrice = 0;
    switch (selectedFrequency) {
      case BillingFrequency.MONTHLY:
        basePrice = tierInfo.price.monthly;
        break;
      case BillingFrequency.QUARTERLY:
        basePrice = tierInfo.price.quarterly;
        break;
      case BillingFrequency.YEARLY:
        basePrice = tierInfo.price.yearly;
        break;
      default:
        basePrice = tierInfo.price.monthly;
    }

    // No additional cost for toolkits - they're included in the Industry Specific tier

    return basePrice * newUserCount;
  };

  const getTierPrice = (tier: SubscriptionTier) => {
    const tierInfo = PRICING_TIERS.find(t => t.tier === tier);
    if (!tierInfo) return 0;

    switch (selectedFrequency) {
      case BillingFrequency.MONTHLY:
        return tierInfo.price.monthly;
      case BillingFrequency.QUARTERLY:
        return tierInfo.price.quarterly;
      case BillingFrequency.YEARLY:
        return tierInfo.price.yearly;
      default:
        return tierInfo.price.monthly;
    }
  };

  const canConfirm = () => {
    if (selectedTier === SubscriptionTier.INDUSTRY_SPECIFIC && !selectedToolkit) {
      return false;
    }
    return true;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Change Subscription Plan
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Billing Frequency */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Billing Frequency</FormLabel>
            <RadioGroup
              row
              value={selectedFrequency}
              onChange={(e) => setSelectedFrequency(e.target.value as BillingFrequency)}
            >
              <FormControlLabel 
                value={BillingFrequency.MONTHLY} 
                control={<Radio />} 
                label="Monthly" 
              />
              <FormControlLabel 
                value={BillingFrequency.QUARTERLY} 
                control={<Radio />} 
                label={
                  <span>
                    Quarterly
                    <Chip
                      label="5% off"
                      size="small"
                      color="success"
                      style={{ marginLeft: 8, height: 20 }}
                    />
                  </span>
                }
              />
              <FormControlLabel
                value={BillingFrequency.YEARLY}
                control={<Radio />}
                label={
                  <span>
                    Yearly
                    <Chip
                      label="10% off"
                      size="small"
                      color="success"
                      style={{ marginLeft: 8, height: 20 }}
                    />
                  </span>
                }
              />
            </RadioGroup>
          </FormControl>

          <Divider />

          {/* Plan Tier */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Subscription Tier</FormLabel>
            <RadioGroup
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value as SubscriptionTier)}
            >
              {PRICING_TIERS.map((tier) => (
                <FormControlLabel
                  key={tier.id}
                  value={tier.tier}
                  control={<Radio />}
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <div style={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {tier.name}
                          {tier.popular && (
                            <Chip
                              label="POPULAR"
                              size="small"
                              color="primary"
                              style={{ marginLeft: 8, height: 20 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tier.description}
                        </Typography>
                      </div>
                      <Typography variant="h6" fontWeight="bold" style={{ marginLeft: 16 }}>
                        ${getTierPrice(tier.tier)}/user/mo
                      </Typography>
                    </div>
                  }
                  style={{ marginBottom: 8 }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Toolkit Selection - Shows automatically when Industry Specific is selected */}
          {selectedTier === SubscriptionTier.INDUSTRY_SPECIFIC && (
            <>
              <Divider />
              <ToolkitSelection
                selectedToolkitId={selectedToolkit}
                onSelect={(toolkit) => {
                  console.log('Setting selected toolkit in ChangePlanDialog:', toolkit);
                  setSelectedToolkit(toolkit);
                }}
                billingFrequency={selectedFrequency === BillingFrequency.YEARLY ? 'annual' : 'monthly'}
              />
              {!selectedToolkit && (
                <Alert severity="warning">
                  Please select an industry toolkit to continue
                </Alert>
              )}
            </>
          )}

          <Divider />

          {/* User Count */}
          <FormControl>
            <FormLabel>Number of Users</FormLabel>
            <TextField
              type="number"
              value={newUserCount}
              onChange={(e) => setNewUserCount(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={{ mt: 1, width: 200 }}
            />
          </FormControl>

          {/* Price Summary */}
          <Alert severity="info">
            <Typography variant="subtitle1" fontWeight="bold">
              Total Monthly Cost: ${calculatePrice().toFixed(2)}
            </Typography>
            {selectedTier === SubscriptionTier.INDUSTRY_SPECIFIC && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedToolkit 
                  ? `Includes ${selectedToolkit.replace('_TOOLKIT', '').replace('_', ' ')} Toolkit at no additional cost`
                  : 'Select a toolkit to complete your Industry Specific subscription'}
              </Typography>
            )}
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={onConfirm}
          disabled={loading || !canConfirm()}
        >
          {loading ? 'Updating...' : 'Confirm Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePlanDialog;